/**
 * Servicio para manejar metadata del documento de Word
 * Usa Custom Properties para almacenar execution_id y workpill_id
 */

/* global Word */

export interface DocumentMetadata {
  executionId?: string;
  workpillId?: string;
  userId?: string;
}

export class DocumentMetadataService {
  
  /**
   * Lee la metadata del documento actual
   */
  static async readMetadata(): Promise<DocumentMetadata> {
    try {
      return await Word.run(async (context) => {
        const properties = context.document.properties.customProperties;
        properties.load("key, value");
        await context.sync();

        const metadata: DocumentMetadata = {};

        // Buscar execution_id
        const executionIdProp = properties.items.find(p => p.key === "execution_id");
        if (executionIdProp) {
          metadata.executionId = executionIdProp.value;
        }

        // Buscar workpill_id
        const workpillIdProp = properties.items.find(p => p.key === "workpill_id");
        if (workpillIdProp) {
          metadata.workpillId = workpillIdProp.value;
        }

        // Buscar user_id
        const userIdProp = properties.items.find(p => p.key === "user_id");
        if (userIdProp) {
          metadata.userId = userIdProp.value;
        }

        console.log('📄 Metadata leída del documento:', metadata);
        return metadata;
      });
    } catch (error) {
      console.error('❌ Error leyendo metadata del documento:', error);
      return {};
    }
  }

  /**
   * Guarda metadata en el documento
   */
  static async saveMetadata(metadata: DocumentMetadata): Promise<boolean> {
    try {
      return await Word.run(async (context) => {
        const properties = context.document.properties.customProperties;
        properties.load("items");
        await context.sync();

        // Función helper para agregar o actualizar una propiedad
        const setProperty = (key: string, value: string) => {
          const existing = properties.items.find(p => p.key === key);
          if (existing) {
            existing.delete();
          }
          properties.add(key, value);
        };

        // Guardar execution_id
        if (metadata.executionId) {
          setProperty("execution_id", metadata.executionId);
        }

        // Guardar workpill_id
        if (metadata.workpillId) {
          setProperty("workpill_id", metadata.workpillId);
        }

        // Guardar user_id
        if (metadata.userId) {
          setProperty("user_id", metadata.userId);
        }

        await context.sync();
        console.log('✅ Metadata guardada en el documento:', metadata);
        return true;
      });
    } catch (error) {
      console.error('❌ Error guardando metadata en el documento:', error);
      return false;
    }
  }

  /**
   * Obtiene el nombre del documento actual
   */
  static async getDocumentName(): Promise<string | null> {
    try {
      return await Word.run(async (context) => {
        const properties = context.document.properties;
        properties.load("title");
        await context.sync();
        return properties.title || "Documento sin título";
      });
    } catch (error) {
      console.error('❌ Error obteniendo nombre del documento:', error);
      return null;
    }
  }

  /**
   * Verifica si el documento tiene metadata de ejecución
   */
  static async hasExecutionMetadata(): Promise<boolean> {
    const metadata = await this.readMetadata();
    return !!metadata.executionId;
  }

  /**
   * Limpia la metadata del documento
   */
  static async clearMetadata(): Promise<boolean> {
    try {
      return await Word.run(async (context) => {
        const properties = context.document.properties.customProperties;
        properties.load("items");
        await context.sync();

        const keysToDelete = ["execution_id", "workpill_id", "user_id"];
        
        keysToDelete.forEach(key => {
          const prop = properties.items.find(p => p.key === key);
          if (prop) {
            prop.delete();
          }
        });

        await context.sync();
        console.log('🧹 Metadata del documento limpiada');
        return true;
      });
    } catch (error) {
      console.error('❌ Error limpiando metadata:', error);
      return false;
    }
  }
}








